import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Office } from '../../../classes/office';
import { SCREEN_SIZE, ContactFormLocationType } from '../../../classes/enums';
import { Listing } from '../../../classes/listing';
import { PageProfileComponent } from '../page-profile.component';
import { DetailStub } from '../../listing-details/detail-section/detail-section.component';
import { Agent } from '../../../classes/agent';
import { Pagination } from '../../../classes/pagination';
import { HistoryItem } from '../../../classes/history-item';
import LatLngLiteral = google.maps.LatLngLiteral;
import { User } from '../../../classes/user';
import { RemaxLeadEntity, MapOfficeToLead } from '../../../classes/leads';
import { skip } from 'rxjs/operators';
import { IBlogResponse, IBlogItem, IInternalLinksResponse } from '../../../classes/interfaces';
import { get } from 'lodash-es';

@Component({
  selector: 'app-office-profile',
  templateUrl: './office-profile.component.html',
  styleUrls: ['./../page-profile.scss', './office-profile.scss']
})
export class OfficeProfileComponent extends PageProfileComponent implements OnInit {
  leadInfo: Partial<RemaxLeadEntity>;
  office: Office = new Office();
  officeAgents: Agent[] = [];
  officeListings: Listing[] = [];
  agentPagination: Pagination;

  latLng: LatLngLiteral;
  specialitiesNumColumns = [];
  maxNumberItemsPerList = 15;
  screenSize: SCREEN_SIZE;

  fastFactRowsDesktopOnly: DetailStub[] = [];
  ContactFormLocationType = ContactFormLocationType;
  ad: IBlogItem;
  @ViewChild('agentList') alElementRef: ElementRef;

  // Enum
  Agent = Agent;

  private _agentPaginationLimitPerPage = 3;
  private _officePaginationLimitPerPage = 10;

  ngOnInit() {
    super.ngOnInit();

    this.addSub(this.activatedRoute.data.subscribe(({ user, office, screenSize }: { user: User, office: Office, screenSize: SCREEN_SIZE }) => {
      this.leadInfo = MapOfficeToLead(office);
      this.setUserDetailsForContactForm(user);
      this._setAgentPaginationForScreenSize(screenSize);
      this._getAllOfficeInfo(office);
    }));

    // First skipped due to resolver
    this.addSub(this.userService.currentUser.pipe(skip(1)).subscribe((user) => this.setUserDetailsForContactForm(user)));

    this.blogService.getByTags(['cms-office-details-side']).toPromise()
      .then((results: IBlogResponse[]) => {
        this.ad = get(results, '[0].content');
      });
  }
  getListSize(index: number): number {
    return Math.min((index + 1) * this.maxNumberItemsPerList, this.office.specialties.length);
  }

  onListingChanged(event: { card: Listing, index: number }) {
    if (this.listingsPagination.pageNumber > this.listingsPagination.totalPages) return;

    // We paginate just before they reach the end of the current listings

    if (event.index >= this.listingsPagination.offset - this.minListingsLeftBeforeRetrievingNew) {
      this.listingsPagination.pageNumber++;
      this.getOfficeListings();
    }
  }

  getOfficeListings(pagination?: Pagination) {
    if (pagination) this.listingsPagination = pagination;
    // this.hasRetrievedSuggestedListings = false;
    this.officeService.getListingsPreviewsForOffice(this.office.personifyId, this.listingsPagination).then(response => {
      this.hasRetrievedSuggestedListings = true;
      this.listingsPagination.setTotalResults(response.totalHits);
      this.officeListings = this.officeListings.concat(response.results);
      this.productSchema = this.listingsService.checkForProductValues(this.officeListings);
    });
  }

  getAgents(pagination: Pagination) {
    this.agentPagination = pagination;
    this.officeService.getAgentsForOffice(this.office.personifyId, this.agentPagination).then(response => {
      this.agentPagination.setTotalResults(response.totalHits);
      this.officeAgents = response.results;
      // TODO: this.hasRetrievedAgents
      this.domManipulationService.scrollIntoView(this.alElementRef, true);
    });
  }

  private _setAgentPaginationForScreenSize(screenSize: SCREEN_SIZE) {
    this._agentPaginationLimitPerPage = screenSize === SCREEN_SIZE.MOBILE_ONLY ? 3 : 8;
    this.screenSize = screenSize;
  }

  private _getAllOfficeInfo(office: Office) {
    if (!office) return;

    this.office = office;
    this.isLoading = false;


    this.metaTagService.setPageTitle('meta-tags.office-profile.title', { city: this.office.city, name: this.office.officeName, province: this.office.state });
    this.metaTagService.setPageDescription('meta-tags.office-profile.description', { name: this.office.officeName });

    this.metaTagService.setOpenGraphTags([
      {
        name: 'title',
        content: 'meta-tags.office-profile.title',
        variables: { city: this.office.city, name: this.office.officeName, province: this.office.state },
        isTranslateKey: true
      },
      {
        name: 'description',
        content: 'meta-tags.office-profile.description',
        variables: { name: this.office.officeName },
        isTranslateKey: true
      },
    ]);

    this.prerenderService.setPreRenderFlag(200);
    // this.office.personifyId = officeId;
    if (this.office.latitude && this.office.longitude) {
      this.latLng = {
        lat: this.office.latitude,
        lng: this.office.longitude
      } as LatLngLiteral;
    }
    this.fastFactRows = [
      new DetailStub({ title: 'page-profile.languages', value: this.office.languages.join(', ') }),
      new DetailStub({ title: 'page-profile.types', value: this.office.propertyTypes.join(', ') }),
    ];
    this.fastFactRowsDesktopOnly = [
      new DetailStub({ title: 'page-profile.languages', value: this.office.languages.join(', ') }),
      new DetailStub({ title: 'page-profile.types', value: this.office.propertyTypes.join(', ') }),
      new DetailStub({ title: 'page-profile.contact', value: this.office.parsedPhoneNumber })
    ];
    // We hack something here to create a useless array initialized with 1

    // TODO SSR: Save Listings, Agents in Transfer State, do not set prerender flag until everything returned.
    if (this.office.specialties) this.specialitiesNumColumns = new Array(Math.round(this.office.specialties.length / this.maxNumberItemsPerList)).map((r) => 1);
    this.getOfficeListings(this.listingsPagination);
    this.getAgents(new Pagination(0, this._agentPaginationLimitPerPage));

    this.historyService.push(new HistoryItem({officeId: '' + this.office.personifyId, detailUrl: this.office.detailUrl}));

    this.getCmsLinks(office.state, office.city);
    // this.getInternalLinks('opp', office.state, office.city);
  }
}
